import { IconButton, type DataTableLoadState } from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ReactNode } from "react";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatDateTime, formatDateTimeSeconds, formatDuration } from "@/utils/datetime";
import {
  timelineRange,
  timelineTicks,
  type TimelinePhase,
  type TimelineRange,
  type WorkerTimeline as Timeline,
} from "./workerTimelineModel";
import styles from "./WorkerTimeline.module.css";

const labels: Record<TimelinePhase, string> = {
  waiting: "대기",
  execution: "실행 경과",
  finished: "종료",
  lastReport: "마지막 보고",
};

export type WorkerTimelineRow = {
  id: string;
  name: string;
  status: ReactNode;
  summary: ReactNode;
  timeline: Timeline;
  onOpen: () => void;
  action?: ReactNode;
  offline?: boolean;
};

/** Worker와 작업의 현재 회차를 같은 시간축에 배치하는 운영 화면이다. */
export function WorkerTimeline({
  rows,
  now,
  rangeKey,
  ariaLabel,
  loadState,
  errorMessage,
  onRetry,
  emptyMessage,
  summary,
  actions,
}: {
  rows: readonly WorkerTimelineRow[];
  now: number;
  rangeKey: string;
  ariaLabel: string;
  // 목록 소스의 조회 상태. 행이 없는 본문이 대기·실패·빈 목록 중 무엇을 알릴지 정한다.
  loadState: DataTableLoadState;
  errorMessage: string | null;
  onRetry: () => void;
  // 조회가 성공했고 행이 없을 때만 표시한다.
  emptyMessage: string;
  summary: ReactNode;
  actions: ReactNode;
}) {
  // 받기 전(idle)은 대기와 같다. 조회가 성공하기 전에는 빈 목록이라고 알리지 않는다.
  const waiting = loadState === "idle" || loadState === "loading";
  const fit = timelineRange(
    rows.map((row) => row.timeline),
    now,
  );
  const [viewport, setViewport] = useState<{ key: string; range: TimelineRange } | null>(null);
  // 폴링은 사용자가 읽고 있는 척도를 바꾸지 않는다. 조회 조건이 바뀔 때만 다시 맞춘다.
  useEffect(() => {
    if (now !== 0)
      setViewport((current) =>
        current?.key === rangeKey
          ? current
          : { key: rangeKey, range: { start: fit.start, end: fit.end } },
      );
  }, [rangeKey, now, fit.start, fit.end]);
  const range = viewport?.key === rangeKey ? viewport.range : fit;
  const span = range.end - range.start;
  const ticks = timelineTicks(range);
  const percent = (at: number) => `${(100 * (at - range.start)) / span}%`;
  const setRange = (next: TimelineRange) => setViewport({ key: rangeKey, range: next });
  // 맞춤 범위 밖에는 그릴 막대가 없다. 그래서 맞춤 범위를 모두 표시하면 축소를 끈다.
  const showsFit = range.start <= fit.start && range.end >= fit.end;
  const zoom = (factor: number) => {
    const nextSpan = Math.max(60_000, span * factor);
    // 맞춤 범위의 폭에 닿는 축소는 맞춤 범위에서 끝낸다.
    if (factor > 1 && nextSpan >= fit.end - fit.start) return setRange(fit);
    const middle = (range.start + range.end) / 2;
    setRange({ start: middle - nextSpan / 2, end: middle + nextSpan / 2 });
  };
  const shift = (direction: number) =>
    setRange({
      start: range.start + (direction * span) / 2,
      end: range.end + (direction * span) / 2,
    });
  const currentVisible = now >= range.start && now <= range.end;
  return (
    <section className={styles.shell} aria-label={ariaLabel}>
      <div className={styles.toolbar}>
        <div className={styles.summary}>
          {summary}
          <WorkerTimelineLegend />
        </div>
        <div className={styles.actions}>{actions}</div>
      </div>
      <div className={styles.navigation}>
        <span className={styles.range} aria-label="표시 시간 범위">
          {now === 0
            ? "서버 기준 시각을 기다리고 있습니다."
            : `${formatDateTime(range.start)} — ${formatDateTime(range.end)}`}
        </span>
        <div className={styles.actions} role="group" aria-label="타임라인 시간 이동">
          <IconButton
            text="이전 시간"
            size="sm"
            variant="ghost"
            disabled={now === 0}
            onClick={() => shift(-1)}
          />
          <IconButton
            text="다음 시간"
            size="sm"
            variant="ghost"
            disabled={now === 0}
            onClick={() => shift(1)}
          />
          <IconButton
            text="확대"
            size="sm"
            variant="surface"
            disabled={now === 0 || span <= 60_000}
            onClick={() => zoom(0.5)}
          />
          <IconButton
            text="축소"
            size="sm"
            variant="surface"
            disabled={now === 0 || showsFit}
            onClick={() => zoom(2)}
          />
          <IconButton
            text="현재"
            size="sm"
            variant="surface"
            disabled={now === 0}
            onClick={() => setRange({ start: now - span * 0.9, end: now + span * 0.1 })}
          />
          <IconButton
            text="맞춤"
            size="sm"
            variant="surface"
            disabled={now === 0}
            onClick={() => setRange(fit)}
          />
        </div>
      </div>
      <div className={styles.viewport} aria-busy={waiting}>
        <div className={styles.grid}>
          <div className={styles.header}>
            <span className={styles.identityHeader}>작업 · 상태</span>
            <div className={styles.axis} aria-label="시간축">
              {ticks.map((tick) => (
                <span
                  className={styles.tickLabel}
                  key={tick}
                  style={{ left: percent(tick) }}
                  title={formatDateTimeSeconds(tick)}
                >
                  {span < 300_000
                    ? formatDateTimeSeconds(tick).slice(11, 19)
                    : span < 86_400_000
                      ? formatDateTime(tick).slice(11)
                      : formatDateTime(tick).slice(5)}
                </span>
              ))}
              {currentVisible && now !== 0 && (
                <span className={styles.nowLabel} style={{ left: percent(now) }}>
                  현재
                </span>
              )}
            </div>
          </div>
          <ol className={styles.rows} aria-label={`${ariaLabel} 작업`}>
            {rows.map((row) => (
              <li className={styles.row} key={row.id} data-offline={row.offline || undefined}>
                <div className={styles.identity}>
                  <button type="button" className={styles.open} onClick={row.onOpen}>
                    <span className={styles.name}>{row.name}</span>
                    <span className={styles.status}>{row.status}</span>
                    <span className={styles.meta}>{row.summary}</span>
                  </button>
                  {row.action}
                </div>
                <button
                  type="button"
                  className={styles.plot}
                  aria-label={`${row.name} 타임라인 상세`}
                  onClick={row.onOpen}
                >
                  {ticks.map((tick) => (
                    <span
                      aria-hidden="true"
                      className={styles.gridline}
                      key={tick}
                      style={{ left: percent(tick) }}
                    />
                  ))}
                  {currentVisible && now !== 0 && (
                    <span
                      aria-hidden="true"
                      className={styles.now}
                      style={{ left: percent(now) }}
                    />
                  )}
                  <WorkerTimelineCell timeline={row.timeline} range={range} />
                </button>
              </li>
            ))}
          </ol>
          {rows.length === 0 && (
            <div className={styles.empty}>
              {waiting ? (
                <LoadingState>작업을 불러오고 있습니다.</LoadingState>
              ) : loadState === "failed" ? (
                <EmptyState tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
                  {errorMessage ?? "작업을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."}
                </EmptyState>
              ) : (
                <EmptyState>{emptyMessage}</EmptyState>
              )}
            </div>
          )}
        </div>
      </div>
      {now !== 0 && (
        <span className={styles.observation}>
          서버 기준 {formatDateTimeSeconds(now)}
          {!currentVisible ? " · 현재 시각이 표시 범위 밖에 있습니다." : ""}
        </span>
      )}
    </section>
  );
}

function WorkerTimelineCell({ timeline, range }: { timeline: Timeline; range: TimelineRange }) {
  const span = range.end - range.start;
  const description = timeline.segments
    .map(
      (segment) =>
        `${labels[segment.phase]} ${formatDateTimeSeconds(segment.start)}${segment.end === segment.start ? "" : ` ~ ${formatDateTimeSeconds(segment.end)} (${formatDuration(segment.end - segment.start)})`}`,
    )
    .join(" · ");
  const visible = timeline.segments.filter(
    (segment) => segment.end >= range.start && segment.start <= range.end,
  );
  return (
    <span className={styles.cell}>
      {visible.length > 0 && (
        <span className={styles.track} role="img" aria-label={description} title={description}>
          {visible.map((segment) => (
            <span
              key={segment.phase}
              className={styles.segment}
              data-phase={segment.phase}
              data-clipped-start={segment.start < range.start || undefined}
              data-clipped-end={segment.end > range.end || undefined}
              style={{
                left: `${(100 * (Math.max(segment.start, range.start) - range.start)) / span}%`,
                width: `${(100 * (Math.min(segment.end, range.end) - Math.max(segment.start, range.start))) / span}%`,
              }}
            />
          ))}
        </span>
      )}
      <span className={styles.note}>
        {timeline.note ??
          (visible.length === 0 && timeline.segments.length > 0
            ? "표시 범위 밖"
            : timeline.segments
                .filter((segment) => segment.end > segment.start)
                .map(
                  (segment) =>
                    `${labels[segment.phase]} ${formatDuration(segment.end - segment.start)}`,
                )
                .join(" · "))}
      </span>
    </span>
  );
}

function WorkerTimelineLegend() {
  return (
    <span className={styles.legend} aria-label="타임라인 범례">
      {(["waiting", "execution", "finished", "lastReport"] as const).map((phase) => (
        <span key={phase}>
          <span className={styles.swatch} data-phase={phase} aria-hidden="true" />
          {labels[phase]}
        </span>
      ))}
    </span>
  );
}
